import config from 'config';

import { Gender } from '~/techstyle-shared/react-accounts';
import { createSelector } from '~/techstyle-shared/redux-core';

import {
  aggregationKeyMap,
  clothingSizes,
  mensPdpTopSizes,
  mensTopSizes,
  getPdpClothingSizes,
  PROFILE_KEYS,
} from '../constants/productSizes';

import { getProfileFilterValue } from './getProfileFilterValue';

/**
 * Selector to retrieve the user's profile sizes as an object mapping
 * aggregation field to filter value.
 */
export const getProfileSizeFilters = createSelector(
  [
    (customer) => customer.profile,
    (customer, gender) => ({ isMale: gender === Gender.MALE, gender }),
    (customer, gender, isPdp) => isPdp,
    (customer, gender, isPdp, tldLocale) => tldLocale,
  ],
  (profile, { isMale, gender }, isPdp, tldLocale) => {
    if (!profile) {
      return null;
    }

    const map = Object.entries(aggregationKeyMap).reduce(
      (aggregationValues, [aggregationField, profileKey]) => {
        const profileValue = profile[profileKey];
        // Assume a value of `0` is valid.
        if (profileValue != null && profileValue !== '') {
          const filterValue = isMale
            ? getMensProfileFilterValue({
                profileKey,
                profileValue,
                isPdp,
                tldLocale,
              })
            : getProfileFilterValue({
                profileKey,
                profileValue,
                isPdp,
                subField: aggregationField,
                tldLocale,
              });

          if (filterValue != null) {
            aggregationValues[aggregationField] = filterValue;
          }
        }
        return aggregationValues;
      },
      {}
    );

    const sizeOverride =
      config.get('public.brand.sizeOverride')?.[gender] ?? {};

    for (const [currentKey, overrideKey] of Object.entries(sizeOverride)) {
      if (map[currentKey]) {
        map[overrideKey] = map[currentKey];
        delete map[currentKey];
      }
    }

    return map;
  }
);

function getMensProfileFilterValue({
  profileKey,
  profileValue,
  isPdp = false,
  locale,
}) {
  const mensPdpBottomSizes = config.get('public.brand.mensPdpBottomSizes');
  const mensBottomSizes = config.get('public.brand.mensBottomSizes');
  switch (profileKey) {
    case PROFILE_KEYS.BOTTOM_SIZE:
      return isPdp
        ? mensPdpBottomSizes[profileValue]
        : mensBottomSizes[profileValue];
    case PROFILE_KEYS.TOP_SIZE:
      return isPdp ? mensPdpTopSizes[profileValue] : mensTopSizes[profileValue];
    case PROFILE_KEYS.BRA_SIZE:
    case PROFILE_KEYS.BRALETTE_SIZE:
    case PROFILE_KEYS.DRESS_SIZE:
    case PROFILE_KEYS.ONE_PIECE_SIZE:
      return isPdp
        ? getPdpClothingSizes(locale)[profileValue]
        : clothingSizes[profileValue];
    case PROFILE_KEYS.SHOE_SIZE:
      // Shoe sizes are already valid as-is.
      return profileValue.toString();
  }
}
